import { Stack } from '@mantine/core';
import { TextInput } from '@repo/foundations';
import { observer } from 'mobx-react-lite';

import { showNotification } from '@mantine/notifications';
import {
	SettingsBanner,
	SettingsBlock,
	SettingsSelect,
} from '@repo/common/components';
import cronstrue from 'cronstrue';
import { useCallback, useState } from 'react';
import { useUpdateIntegration } from '../../api/hooks/integration';
import { getCronExpression, isValidCron } from './IntegrationSchedule/utils';

const FREQUENCY_OPTIONS = [
	{
		label: 'Hourly',
		value: 'hourly',
	},
	{
		label: 'Daily',
		value: 'daily',
	},
	{
		label: 'Weekly',
		value: 'weekly',
	},
	{
		label: 'Custom (cron)',
		value: 'custom',
	},
];

const DAYS_OF_WEEK = [
	{
		label: 'Monday',
		value: '1',
	},
	{
		label: 'Tuesday',
		value: '2',
	},
	{
		label: 'Wednesday',
		value: '3',
	},
	{
		label: 'Thursday',
		value: '4',
	},
	{
		label: 'Friday',
		value: '5',
	},
	{
		label: 'Saturday',
		value: '6',
	},
	{
		label: 'Sunday',
		value: '7',
	},
];

const HOURS_OF_DAY = [
	{
		label: '12 AM UTC',
		value: '0',
	},
	{
		label: '1 AM UTC',
		value: '1',
	},
	{
		label: '2 AM UTC',
		value: '2',
	},
	{
		label: '3 AM UTC',
		value: '3',
	},
	{
		label: '4 AM UTC',
		value: '4',
	},
	{
		label: '5 AM UTC',
		value: '5',
	},
	{
		label: '6 AM UTC',
		value: '6',
	},
	{
		label: '7 AM UTC',
		value: '7',
	},
	{
		label: '8 AM UTC',
		value: '8',
	},
	{
		label: '9 AM UTC',
		value: '9',
	},
	{
		label: '10 AM UTC',
		value: '10',
	},
	{
		label: '11 AM UTC',
		value: '11',
	},
	{
		label: '12 PM UTC',
		value: '12',
	},
	{
		label: '1 PM UTC',
		value: '13',
	},
	{
		label: '2 PM UTC',
		value: '14',
	},
	{
		label: '3 PM UTC',
		value: '15',
	},
	{
		label: '4 PM UTC',
		value: '16',
	},
	{
		label: '5 PM UTC',
		value: '17',
	},
	{
		label: '6 PM UTC',
		value: '18',
	},
	{
		label: '7 PM UTC',
		value: '19',
	},
	{
		label: '8 PM UTC',
		value: '20',
	},
];

const DEFAULT_FREQUENCY = 'weekly';
const DEFAULT_DAY = '5'; // Friday
const DEFAULT_TIME = '3'; // 3 AM
const DEFAULT_MESSAGE = 'This sync will run at 03:00 AM, only on Friday';
const DEFAULT_CRON_EXPRESSION = '0 0 * * 6';

export const IntegrationSchedulePicker = observer(
	({ integrationId }: { integrationId: string }) => {
		const [frequency, setFrequency] = useState<string>(DEFAULT_FREQUENCY);
		const [day, setDay] = useState<string>(DEFAULT_DAY);
		const [time, setTime] = useState<string>(DEFAULT_TIME);
		const [cronExpression, setCronExpression] = useState<string>(
			DEFAULT_CRON_EXPRESSION
		);
		const [message, setMessage] = useState<string>(DEFAULT_MESSAGE);

		const { mutateAsync: updateIntegration } = useUpdateIntegration({});

		const showCronExpression = frequency === 'custom';
		const showDayInput = frequency === 'weekly';
		const showHourInput = ['daily', 'weekly'].includes(frequency);

		const setIntegrationSchedule = useCallback(
			async (cron: string) => {
				try {
					await updateIntegration({
						data: {
							id: integrationId,
							scheduled_extractions_cron: cron,
						},
					});
					setCronExpression(cron);
					showNotification({
						message: `Schedule updated`,
						color: 'green',
					});

					const cronString = cronstrue.toString(cron);
					const formattedCronString =
						cronString.charAt(0).toLowerCase() + cronString.slice(1);
					setMessage(`This sync will run ${formattedCronString}`);
				} catch {
					showNotification({
						message: `Schedule update failed`,
						color: 'red',
					});
					setMessage(`Schedule update failed`);
				}
			},
			[integrationId, updateIntegration, setCronExpression, setMessage]
		);

		const onChangeFrequency = useCallback(
			(value: string) => {
				setFrequency(value || DEFAULT_FREQUENCY);
				setIntegrationSchedule(getCronExpression(value, day, time));
			},
			[day, time, setIntegrationSchedule]
		);

		const onChangeDay = useCallback(
			(value: string) => {
				setDay(value || DEFAULT_DAY);
				setIntegrationSchedule(getCronExpression(frequency, value, time));
			},
			[frequency, time, setIntegrationSchedule]
		);

		const onChangeTime = useCallback(
			(value: string) => {
				setTime(value || DEFAULT_TIME);
				setIntegrationSchedule(getCronExpression(frequency, day, value));
			},
			[frequency, day, setIntegrationSchedule]
		);

		const onChangeCronExpression = useCallback(
			async (value: string) => {
				setCronExpression(value);

				if (isValidCron(value, true)) {
					setIntegrationSchedule(value);
				}
			},
			[setIntegrationSchedule]
		);

		return (
			<Stack spacing={0}>
				<SettingsSelect
					title="Frequency"
					options={FREQUENCY_OPTIONS}
					onChange={(value) => onChangeFrequency(value || DEFAULT_FREQUENCY)}
					searchable={false}
					value={frequency}
				/>
				{showDayInput && (
					<SettingsSelect
						title="Day"
						options={DAYS_OF_WEEK}
						onChange={(value) => onChangeDay(value || DEFAULT_DAY)}
						searchable={false}
						value={day}
					/>
				)}
				{showHourInput && (
					<SettingsSelect
						title="Time"
						options={HOURS_OF_DAY}
						onChange={(value) => onChangeTime(value || DEFAULT_TIME)}
						searchable={false}
						value={time}
					/>
				)}
				{showCronExpression && (
					<SettingsBlock
						title="Cron expression"
						description="Cron syntax: minute hour day month weekday. Learn more at crontab.guru"
						rightComponent={
							<TextInput
								onChange={(event) =>
									onChangeCronExpression(event.currentTarget.value)
								}
								placeholder={DEFAULT_CRON_EXPRESSION}
								style={{ width: '108px' }}
								size="sm"
								value={cronExpression}
							/>
						}
					/>
				)}
				<SettingsBanner message={message} />
			</Stack>
		);
	}
);
